import { useSelector } from 'react-redux';
import { IMusselLineResource } from '../../../entities/farms.entities';
import { translate } from '../../../lib/lang.helper';
import { Input } from '../../shared';
import { selectLang } from '../../../store/ui/ui.selector';
import { SelectFarmsByFarmID } from '../../../store/extra/extra.selector';
import { numToStr, getUnitOfSpecie } from '../../../lib/common.helpers';

interface IForm {
  line_length: number | null;
  drop?: number;
  submersion?: number;
  spacing?: number;
}

interface Props {
  lineData: IMusselLineResource;
  form: IForm;
  onChange: (form: any) => void;
  errors?: any;
  setErrors?: (errors: any) => void;
  disabled?: boolean;
  unitType?: 'METRIC' | 'IMPERIAL' | null;
}

const MusselSeedLengthForm = ({
  lineData,
  form,
  onChange,
  errors,
  setErrors,
  disabled,
  unitType,
}: Props) => {
  const lang = useSelector(selectLang);
  const farmsData = SelectFarmsByFarmID(lineData.farm_id).find(
    x => x.id === lineData.farm_id,
  );

  const handleChange = (key: keyof IForm, value: string) => {
    let tmp = { ...form };
    if (key === 'line_length') {
      tmp[key] = value.length > 0 ? Number(value) : null;
    } else if (key === 'spacing') {
      tmp[key] = value.length > 0 ? Number(value) : undefined;
      if (lineData.line_type === 'DROPPERS' && tmp.spacing) {
        tmp.line_length = Math.round(lineData.backbone / tmp.spacing);
      }
    } else {
      tmp[key] = value.length > 0 ? Number(value) : undefined;
    }
    onChange(tmp);
    if (setErrors) {
      setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
    }
  };

  return lineData.line_type === 'LONGLINE' ? (
    <>
      <div className={`mb-16 ${errors?.line_length ? 'invalid-form' : ''}`}>
        <Input
          label={translate(lang, 'Line Length')}
          type='number'
          value={numToStr(form.line_length)}
          unit={getUnitOfSpecie(unitType, 'distance_m')}
          required
          onChange={e => handleChange('line_length', e.target.value)}
          disabled={disabled}
        />
        {errors?.line_length && (
          <div className='invalid-feedback'>{errors?.line_length}</div>
        )}
      </div>
      <div className='d-flex mb-17'>
        <div className='mr-3 w-100'>
          <Input
            label={translate(lang, 'Drop')}
            type='number'
            value={numToStr(form.drop)}
            unit={getUnitOfSpecie(unitType, 'distance_m')}
            required
            onChange={e => handleChange('drop', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className='ml-3 mr-3 w-100'>
          <Input
            label={translate(lang, 'Submersion')}
            type='number'
            value={numToStr(form.submersion)}
            unit={getUnitOfSpecie(unitType, 'distance_m')}
            required
            onChange={e => handleChange('submersion', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className='ml-3 w-100'>
          <Input
            label={translate(lang, 'Spacing')}
            type='number'
            value={numToStr(form.spacing)}
            unit={getUnitOfSpecie(unitType, 'size_mm')}
            required
            onChange={e => handleChange('spacing', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  ) : lineData.line_type === 'DROPPERS' ? (
    <>
      <div className='d-flex mb-16'>
        <div className='w-100 mr-12'>
          <Input
            label={translate(lang, 'Droppers length')}
            type='number'
            value={numToStr(form.drop)}
            onChange={e => handleChange('drop', e.target.value)}
            unit={getUnitOfSpecie(unitType, 'distance_m')}
            disabled={disabled}
          />
        </div>
        <div className='w-100 ml-12'>
          <Input
            label={translate(lang, 'Droppers spacing')}
            type='number'
            value={numToStr(form.spacing)}
            onChange={e => handleChange('spacing', e.target.value)}
            unit={getUnitOfSpecie(unitType, 'distance_m')}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={`mb-16 ${errors?.line_length ? 'invalid-form' : ''}`}>
        <Input
          label={translate(lang, 'Number of droppers')}
          type='number'
          value={numToStr(form.line_length)}
          required
          onChange={e => handleChange('line_length', e.target.value)}
          disabled={disabled}
        />
        {!!form.spacing && !!lineData.backbone && (
          <div className='mt-3' style={{ color: '#721c24' }}>
            {translate(
              lang,
              'Based on %s length you can have %s droppers',
              farmsData?.structure === 'RAFT' ? 'beam' : 'backbone',
              Math.max(
                0,
                Math.round(lineData.backbone / form.spacing) -
                  (lineData.growing_cycle?.current_line_length ?? 0),
              ),
            )}
          </div>
        )}
        {errors?.line_length && (
          <div className='mt03'>{errors?.line_length}</div>
        )}
      </div>
    </>
  ) : lineData.line_type === 'PLATFORM' ? (
    <>
      <div className={`mb-16 ${errors?.line_length ? 'invalid-form' : ''}`}>
        <Input
          label={translate(lang, 'Number of droppers')}
          type='number'
          value={numToStr(form.line_length)}
          required
          onChange={e => handleChange('line_length', e.target.value)}
          disabled={disabled}
        />
        {errors?.line_length && (
          <div className='mt03'>{errors?.line_length}</div>
        )}
      </div>
      <div className='d-flex mb-16'>
        <div className='w-100 mr-12'>
          <Input
            label={translate(lang, 'Droppers length')}
            type='number'
            value={numToStr(form.drop)}
            onChange={e => handleChange('drop', e.target.value)}
            unit='m'
            disabled={disabled}
          />
        </div>
        <div className='w-100 ml-12'>
          <Input
            label={translate(lang, 'Droppers spacing')}
            type='number'
            value={numToStr(form.spacing)}
            onChange={e => handleChange('spacing', e.target.value)}
            unit='m'
            disabled={disabled}
          />
        </div>
      </div>
    </>
  ) : (
    <></>
  );
};

export default MusselSeedLengthForm;
